import { useQuery } from "@tanstack/react-query";
import { useParams, Link, Navigate } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import { sanityClient, urlFor } from "@/lib/sanityClient";
import logoH from "@/assets/logo_h_3d.png";
import { CalendarBlank, ArrowLeft } from "@phosphor-icons/react";

const POST_QUERY = `*[_type == "post" && slug.current == $slug][0] {
  _id,
  title,
  publishedAt,
  mainImage,
  excerpt,
  body,
  "author": author->{ name, image, bio }
}`;

const portableComponents = {
  types: {
    image: ({ value }) => (
      <figure className="my-8">
        <img
          src={urlFor(value).width(800).url()}
          alt={value.alt || ""}
          className="w-full rounded-2xl object-cover shadow-sm"
          loading="lazy"
        />
        {value.caption && (
          <figcaption className="text-center text-sm text-slate-400 mt-2 italic">
            {value.caption}
          </figcaption>
        )}
      </figure>
    ),
  },
  marks: {
    link: ({ children, value }) => (
      <a
        href={value.href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-amber-600 dark:text-amber-400 underline underline-offset-2 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
      >
        {children}
      </a>
    ),
    strong: ({ children }) => <strong className="font-bold text-slate-900 dark:text-white">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
  },
  block: {
    normal: ({ children }) => (
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4 text-base">{children}</p>
    ),
    h2: ({ children }) => (
      <h2 className="font-manrope text-2xl font-bold text-slate-900 dark:text-white mt-10 mb-4 leading-tight">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="font-manrope text-xl font-bold text-slate-900 dark:text-white mt-8 mb-3 leading-tight">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="font-manrope text-lg font-semibold text-slate-800 dark:text-slate-100 mt-6 mb-2">{children}</h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-amber-400 pl-5 my-6 italic text-slate-600 dark:text-slate-400 bg-amber-50 dark:bg-amber-950/20 py-3 pr-4 rounded-r-xl">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-outside pl-6 mb-4 space-y-1.5 text-slate-700 dark:text-slate-300">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-outside pl-6 mb-4 space-y-1.5 text-slate-700 dark:text-slate-300">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed">{children}</li>,
  },
};

function SkeletonPost() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl" />
      <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3" />
      <div className="space-y-3 mt-6">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className={`h-4 bg-slate-100 dark:bg-slate-700 rounded ${i % 3 === 0 ? "w-4/5" : "w-full"}`} />
        ))}
      </div>
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["sanity-post", slug],
    queryFn: () => sanityClient.fetch(POST_QUERY, { slug }),
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });

  const dateStr = post?.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : null;

  if (!isLoading && !isError && post === null) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 font-outfit">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-slate-100 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1 select-none">
            <img src={logoH} alt="HostFolio" className="w-8 h-8 object-contain" />
            <span className="font-manrope text-lg font-bold">
              <span className="text-slate-800 dark:text-white">Host</span>
              <span className="text-amber-500">Folio</span>
            </span>
          </Link>
          <Link
            to="/?ref=blog-post"
            className="text-sm font-semibold px-4 py-1.5 rounded-xl bg-amber-400 hover:bg-amber-500 text-white transition"
          >
            Try Free →
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 transition mb-8"
        >
          <ArrowLeft size={14} />
          All posts
        </Link>

        {isLoading ? (
          <SkeletonPost />
        ) : isError ? (
          <div className="text-center py-20 text-slate-400">
            <p>Couldn't load this post. <Link to="/blog" className="text-amber-500 underline">Go back to blog</Link></p>
          </div>
        ) : post ? (
          <article>
            {/* Hero image */}
            {post.mainImage && (
              <img
                src={urlFor(post.mainImage).width(800).height(420).fit("crop").url()}
                alt={post.title}
                className="w-full h-56 md:h-80 object-cover rounded-2xl mb-8 shadow-sm"
                loading="eager"
              />
            )}

            {/* Title + meta */}
            <h1 className="font-manrope text-3xl md:text-4xl font-bold text-slate-900 dark:text-white leading-tight tracking-tight mb-4">
              {post.title}
            </h1>

            <div className="flex items-center gap-3 mb-8 pb-8 border-b border-slate-100 dark:border-slate-800">
              {post.author?.image && (
                <img
                  src={urlFor(post.author.image).width(40).height(40).fit("crop").url()}
                  alt={post.author.name}
                  className="w-9 h-9 rounded-full object-cover ring-2 ring-amber-100"
                />
              )}
              <div>
                {post.author?.name && (
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{post.author.name}</p>
                )}
                {dateStr && (
                  <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                    <CalendarBlank size={11} />
                    {dateStr}
                  </p>
                )}
              </div>
            </div>

            {/* Body */}
            {post.excerpt && (
              <p className="text-lg text-slate-600 dark:text-slate-400 font-medium leading-relaxed mb-8 italic border-l-4 border-amber-300 pl-4">
                {post.excerpt}
              </p>
            )}

            {post.body && (
              <div className="prose-custom">
                <PortableText value={post.body} components={portableComponents} />
              </div>
            )}

            {/* Footer CTA */}
            <div className="mt-16 p-6 bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 rounded-2xl text-center">
              <p className="font-manrope text-lg font-bold text-slate-900 dark:text-white mb-1">
                Manage your Airbnb like a pro
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                Automated messages, GST compliance, dynamic pricing — all in one place.
              </p>
              <Link
                to="/?ref=blog-post-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-400 hover:bg-amber-500 text-white font-semibold text-sm rounded-xl transition"
              >
                Try HostFolio free →
              </Link>
            </div>
          </article>
        ) : null}
      </div>
    </div>
  );
}
